import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  IconButton,
  Input,
  Text,
  Badge,
  useBreakpointValue,
  Stack,
  Card,
  CardBody,
  HStack,
  VStack,
  Flex,
} from '@chakra-ui/react';
import FloatingAddButton from '../../components/FloatingAddButton';
import TableSkeleton from '../../components/TableSkeleton';
import { useEffect, useMemo, useState } from 'react';
import { MdAdd, MdAccessTime, MdEdit } from 'react-icons/md';
import { createCausa, uploadCausaFiles, getCausa,listCausas,updateCausa,createSeguimientoByExpediente,createSeguimiento } from '../../services/causasApi';
import { useToast } from '@chakra-ui/react';

import ModalCausa from './ModalCausa';
import ModalSeguimiento from './ModalSeguimiento';
import ModalListaSeguimientos from './ModalListaSeguimientos';

import type { CausaJudicial, SeguimientoCausa, EstadoCausa } from './types';

export default function TablaCausas() {
  const [causas, setCausas] = useState<CausaJudicial[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [causaEditando, setCausaEditando] = useState<CausaJudicial | null>(null);
  const [filtro, setFiltro] = useState('');
const [modalInstance, setModalInstance] = useState(0);
  const isMobile = useBreakpointValue({ base: true, md: false });

  const [seguimientos, setSeguimientos] = useState<SeguimientoCausa[]>([]);
  const [expedienteSeleccionado, setExpedienteSeleccionado] = useState<string | null>(null);
  const [modalSegOpen, setModalSegOpen] = useState(false);
  const [modalVerSegOpen, setModalVerSegOpen] = useState(false);

  const norm = (v?: string) => (v || '').toLowerCase();
const toast = useToast();

  const causasFiltradas = useMemo(() => {
    const f = norm(filtro);
    if (!f) return causas;
    return causas.filter((c) => {
      const descTxt = (c.descripcionDetalladaHtml || '')
        .replace(/<[^>]+>/g, ' ')
        .toLowerCase();
      return (
        norm(c.infractor).includes(f) ||
        norm(c.denunciante).includes(f) ||
        norm(c.tipoCausa).includes(f) ||
        descTxt.includes(f)
      );
    });
  }, [causas, filtro]);
const handleGuardarSeguimientox = async (s: SeguimientoCausa) => {
  try {
    // Llama al backend (por expediente)
    console.log('Guardando seguimiento:', s);

    const created = await createSeguimiento(Number(s.id), {
      fecha: s.fecha,                 // "YYYY-MM-DDTHH:mm"
      nota: s.nota ?? null,
      estado_nuevo: s.estado_nuevo ?? null,
      firmado: s.firmado,
    });
     if (s.estado_nuevo) {
      setCausas(prev =>
        prev.map(c =>
          c.expediente === s.causaExpediente ? { ...c, estado: s.estado_nuevo! } : c
        )
      );
    }
    toast({ title: 'Seguimiento guardado', status: 'success' });
    await fetchCausas();
  } catch (e: any) {
    toast({
      title: 'Error guardando seguimiento',
      description: e.message,
      status: 'error',
    });
  }
};

  const fmtFecha = (iso?: string) => {
    if (!iso) return '—';
    const d = new Date(iso);
    return isNaN(d.getTime())
      ? iso
      : d.toLocaleDateString('es-MX', { year: 'numeric', month: '2-digit', day: '2-digit' });
  };

  const colorEstado = (estado?: string) => {
    const e = norm(estado);
    if (e.includes('archivada') || e.includes('cerrada')) return 'gray';
    if (e.includes('en revisión')) return 'orange';
    return 'green';
  };
const toIdNumber = (v: unknown): number => {
  const n = typeof v === 'string' ? parseInt(v, 10) : (v as number);
  if (!Number.isFinite(n)) throw new Error('ID inválido');
  return n;
};

  const handleGuardarCausa = (nueva: CausaJudicial) => {
    setCausas((prev) => {
      const idx = prev.findIndex((c) => c.id === nueva.id);
      if (idx >= 0) {
        const copia = [...prev];
        copia[idx] = nueva;
        return copia;
      }
      return [...prev, nueva];
    });
    setCausaEditando(null);
    setModalOpen(false);
  };

  // AHORA: estado_nuevo ya es EstadoCausa | undefined, así que asignamos directo
  const handleGuardarSeguimiento = (s: SeguimientoCausa) => {
    setSeguimientos((prev) => [...prev, s]);
    if (s.estado_nuevo) {
      setCausas((prev) =>
        prev.map((c) =>
          c.expediente === s.causaExpediente ? { ...c, estado: s.estado_nuevo as EstadoCausa } : c
        )
      );
    }
  };
async function fetchCausas() {
  try {
    setLoading(true);
    const list = await listCausas();
    setCausas(list);
  } catch (e: any) {
    toast({ title: 'Error cargando causas', description: e.message, status: 'error' });
  } finally {
    setLoading(false);
  }
}

useEffect(() => {
  fetchCausas();
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, []);

// En tu handleGuardarBackend, tras crear, refresca:
async function handleGuardarBackend(c: CausaJudicial) {
  try {
    const isEditing = c.id !== undefined && c.id !== null && c.id !== 0;
    let savedId: number;

    if (isEditing) {
      savedId = toIdNumber(c.id);                 // ✅ number real
     
      // console.log(c)
      await updateCausa(savedId, c);
    } else {
      // no envíes un id inventado al crear
      const { id: newId } = await createCausa({ ...c, id: undefined } as CausaJudicial);
      savedId = toIdNumber(newId);                // ✅ number real
    }

    const acta  = c.evidencias?.actaInfraccionPdf ?? null;
    const pdfs  = (c.evidencias?.pdfs as File[]) ?? [];
    const orden = (c as any).ordenGruaPdf ?? null;

    if (acta || orden || (pdfs && pdfs.length)) {
      await uploadCausaFiles(savedId, { acta, orden, pdfs });  // ✅ number
    }

    const full = await getCausa(savedId);                      // ✅ number

    setCausas(prev => {
      const idx = prev.findIndex(x => toIdNumber(x.id as any) === savedId);
      if (idx === -1) return [...prev, full];
      const copy = [...prev];
      copy[idx] = full;
      return copy;
    });

    toast({ title: 'Causa guardada', description: `ID ${savedId}`, status: 'success' });
    setModalOpen(false);
    setCausaEditando(null);
  } catch (e: any) {
    toast({ title: 'Error', description: e?.message ?? 'No se pudo guardar', status: 'error' });
  }
}

  const ultimoExpedienteNum = useMemo(() => {
    const nums = causas
      .map((c) => {
        const m = c.expediente?.match(/(\d+)$/);
        return m ? parseInt(m[1], 10) : NaN;
      })
      .filter((n) => !isNaN(n)) as number[];
    return nums.length ? Math.max(...nums) : causas.length;
  }, [causas]);

  return (
    <Box p={4} pt={79} position="relative">
      <Text fontSize="2xl" fontWeight="bold" mb={4} color="#5b182b">
        Causas Judiciales
      </Text>

      <Input
        placeholder="Buscar por infractor, denunciante, tipo o descripción"
        value={filtro}
        onChange={(e) => setFiltro(e.target.value)}
        mb={4}
        borderColor="#bb8c52"
        _focus={{ borderColor: "#5b182b", boxShadow: "0 0 0 1px #5b182b" }}
      />

      {loading ? (
        <Box bg="white" borderRadius="lg" shadow="sm" overflow="hidden">
          <TableSkeleton rows={8} columns={6} />
        </Box>
      ) : (
        <Box bg="white" borderRadius="lg" shadow="sm" overflow="hidden">
          <Table variant="simple">
            <Thead bg="#5b182b">
              <Tr>
                <Th color="white">Expediente</Th>
                <Th color="white">Tipo</Th>
                <Th color="white">Fecha</Th>
                <Th color="white">Infractor</Th>
                <Th color="white">Estado</Th>
                <Th color="white">Acciones</Th>
              </Tr>
            </Thead>
            <Tbody>
              {causasFiltradas.map((c) => (
                <Tr key={c.id} _hover={{ bg: "gray.50" }}>
                  <Td fontWeight="medium" color="#5b182b">{c.expediente}</Td>
                  <Td color="#681a2d">{c.tipoCausa}</Td>
                  <Td>{fmtFecha(c.fechaIngreso)}</Td>
                  <Td>{c.infractor || '—'}</Td>
                  <Td>
                    <Badge colorScheme={colorEstado(c.estado)} px={2} py={1} borderRadius="full">
                      {c.estado || 'Abierta'}
                    </Badge>
                  </Td>
                  <Td>
                    <HStack spacing={1}>
                      <IconButton
                        aria-label="Editar causa"
                        icon={<MdEdit />}
                        size="sm"
                        bg="#5b182b"
                        color="white"
                        _hover={{ bg: "#691f30" }}
                        onClick={() => {
                          setCausaEditando(c);
                          setModalOpen(true);
                        }}
                      />
                      <IconButton
                        aria-label="Ver seguimientos"
                        icon={<MdAccessTime />}
                        size="sm"
                        bg="#bb8c52"
                        color="white"
                        _hover={{ bg: "#a67c47" }}
                        onClick={() => {
                          setExpedienteSeleccionado(c.id ? String(c.id) : null);
                          setModalVerSegOpen(true);
                        }}
                      />
                      <IconButton
                        aria-label="Agregar seguimiento"
                        icon={<MdAdd />}
                        size="sm"
                        bg="#681a2d"
                        color="white"
                        _hover={{ bg: "#5b182b" }}
                        onClick={() => {
                          setExpedienteSeleccionado(c.id ? String(c.id) : null);
                          setModalSegOpen(true);
                        }}
                      />
                    </HStack>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Box>
      )}

      {/* Modal Causa */}
      <ModalCausa
        key={`modal-causa-${modalInstance}-${causaEditando?.id ?? 'new'}`} // 👈 remount real

        isOpen={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setCausaEditando(null);
        }}
          onGuardar={handleGuardarBackend}   // 👈 ahora pega al backend

        causa={causaEditando}
        ultimoExpediente={ultimoExpedienteNum}
      />

      {/* Modal Seguimiento nuevo */}
      <ModalSeguimiento
        isOpen={modalSegOpen}
        onClose={() => setModalSegOpen(false)}
        expediente={expedienteSeleccionado || ''}
        onGuardar={handleGuardarSeguimientox}
      />

      {/* Modal lista de seguimientos */}
   <ModalListaSeguimientos
  isOpen={modalVerSegOpen}
  onClose={() => setModalVerSegOpen(false)}
  expediente={expedienteSeleccionado || ''}
/>

      <FloatingAddButton 
        text="Nueva Causa" 
        onClick={() => {
          setModalInstance((k) => k + 1);
          setModalOpen(true);
          setCausaEditando(null);
        }} 
      />
    </Box>
  );
}
